/**
 * Admin → Industry page (Part 2 of Feb 2026 upgrade).
 *
 * Two concerns in one place:
 *   1. Industry picker — change the account's industry at any time.
 *      Hitting Save with "re-seed presets" ticked wipes preset roles /
 *      job types and re-seeds from the new industry. Owner-added
 *      records are left alone.
 *   2. Flat-rate rate card — a two-column table (job_type → amount)
 *      used by HVAC employees whose `pay_structures.flat_rate` is on.
 *      Non-HVAC tenants can ignore it; it does not affect the FLSA
 *      payroll engine.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Wrench, Droplets, Zap as ElectricIcon, Hammer, HelpCircle, Check, Trash2, Plus, Save } from "lucide-react";

const INDUSTRY_CARDS = [
  { code: "hvac",                 label: "HVAC",                                      Icon: Wrench },
  { code: "plumbing_electrical",  label: "Plumbing & Electrical",                     Icon: ElectricIcon },
  { code: "exterior_cleaning",    label: "Exterior Cleaning / Pressure Washing",      Icon: Droplets },
  { code: "roofing_landscaping",  label: "Roofing & Landscaping",                     Icon: Hammer },
  { code: "other",                label: "Other",                                     Icon: HelpCircle },
];

export default function AdminIndustry() {
  const [industry, setIndustry] = useState("");
  const [preset, setPreset] = useState({ roles: [], job_types: [], worker_label_singular: "" });
  const [reseed, setReseed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [entries, setEntries] = useState([]);
  const [newJob, setNewJob] = useState("");
  const [newAmount, setNewAmount] = useState("");
  const [savingCard, setSavingCard] = useState(false);

  const load = async () => {
    try {
      const [a, b] = await Promise.all([
        api.get("/companies/me/industry"),
        api.get("/companies/me/flat-rate-card"),
      ]);
      setIndustry(a.data.industry || "other");
      setPreset(a.data.preset || {});
      setEntries(b.data.entries || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load industry settings");
    }
  };
  useEffect(() => { load(); }, []);

  const saveIndustry = async (code) => {
    setSaving(true);
    try {
      const { data } = await api.patch("/companies/me/industry", { industry: code, reseed_presets: reseed });
      setIndustry(data.industry);
      setPreset(data.preset || {});
      toast.success(`Industry saved · ${code}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const addEntry = () => {
    const jt = newJob.trim();
    const amt = parseFloat(newAmount);
    if (!jt) { toast.error("Enter a job type"); return; }
    if (!(amt >= 0)) { toast.error("Amount must be a number ≥ 0"); return; }
    if (entries.some((e) => e.job_type.toLowerCase() === jt.toLowerCase())) {
      toast.error("That job type already has a rate"); return;
    }
    setEntries([...entries, { job_type: jt, amount: amt }]);
    setNewJob(""); setNewAmount("");
  };
  const removeEntry = (idx) => setEntries(entries.filter((_, i) => i !== idx));
  const saveCard = async () => {
    setSavingCard(true);
    try {
      await api.put("/companies/me/flat-rate-card", { entries });
      toast.success(`Rate card saved · ${entries.length} job type${entries.length === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSavingCard(false);
    }
  };

  const presetJobTypes = useMemo(() => preset.job_types || [], [preset]);

  return (
    <div className="space-y-8" data-testid="admin-industry-page">
      <div>
        <h1 className="text-3xl md:text-4xl font-extrabold font-display tracking-tighter text-slate-900">Industry & HVAC pay</h1>
        <p className="mt-2 text-slate-600 text-[15px] max-w-2xl">
          Pick the industry your business is in. We'll pre-load the right roles and job types.
          HVAC tenants get the flat-rate rate card below too — it feeds every tech whose pay structure includes "flat-rate per job."
        </p>
      </div>

      <Card className="rounded-2xl border-slate-200">
        <CardContent className="p-6 space-y-4">
          <h2 className="text-xl font-bold font-display">Your industry</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="industry-cards">
            {INDUSTRY_CARDS.map((card) => {
              const Icon = card.Icon;
              const selected = industry === card.code;
              return (
                <button
                  key={card.code}
                  type="button"
                  disabled={saving}
                  onClick={() => saveIndustry(card.code)}
                  data-testid={`admin-industry-card-${card.code}`}
                  className={`flex items-center gap-3 p-3.5 rounded-xl border-2 text-left transition disabled:opacity-60 ${
                    selected ? "border-sky-600 bg-sky-50 ring-2 ring-sky-200" : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                >
                  <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${selected ? "bg-sky-600 text-white" : "bg-slate-100 text-slate-700"}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={`font-bold text-sm ${selected ? "text-sky-900" : "text-slate-900"}`}>{card.label}</p>
                  </div>
                  {selected && <Check className="h-4 w-4 text-sky-600 shrink-0" />}
                </button>
              );
            })}
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-600 pt-2 cursor-pointer" data-testid="industry-reseed-wrap">
            <Checkbox checked={reseed} onCheckedChange={setReseed} data-testid="industry-reseed-checkbox" />
            Also re-seed roles and job types from this industry (owner-added items are kept)
          </label>
          {presetJobTypes.length > 0 && (
            <div className="pt-3 border-t border-slate-100 text-xs text-slate-600">
              <p className="font-semibold text-slate-700 mb-1">Preset job types for this industry:</p>
              <p>{presetJobTypes.join(" · ")}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {industry === "hvac" && (
        <Card className="rounded-2xl border-slate-200" data-testid="flat-rate-card-section">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-xl font-bold font-display">Flat-rate rate card</h2>
                <p className="text-sm text-slate-600 mt-0.5">
                  Fixed pay per job type for techs on flat-rate. Entry-level overrides on each job can bump a specific job higher.
                </p>
              </div>
              <Button onClick={saveCard} disabled={savingCard} className="bg-sky-600 hover:bg-sky-700" data-testid="save-rate-card-btn">
                <Save className="h-4 w-4 mr-1.5" /> {savingCard ? "Saving…" : "Save rate card"}
              </Button>
            </div>
            <div className="rounded-xl border border-slate-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-2 font-semibold">Job type</th>
                    <th className="text-right px-4 py-2 font-semibold">Flat pay ($)</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e, idx) => (
                    <tr key={idx} className="border-t border-slate-100" data-testid={`rate-row-${idx}`}>
                      <td className="px-4 py-2 text-slate-900">{e.job_type}</td>
                      <td className="px-4 py-2 text-right tabular-nums font-semibold">${Number(e.amount).toFixed(2)}</td>
                      <td className="px-2 py-2 text-right">
                        <button type="button" onClick={() => removeEntry(idx)} className="text-slate-400 hover:text-red-600" data-testid={`rate-remove-${idx}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {entries.length === 0 && (
                    <tr><td colSpan={3} className="px-4 py-6 text-center text-slate-400 text-sm" data-testid="rate-empty">No flat-rate entries yet. Add one below.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="grid grid-cols-[1fr_120px_auto] gap-2 items-end">
              <div>
                <Label>Job type</Label>
                <Input value={newJob} onChange={(e) => setNewJob(e.target.value)} placeholder="e.g. Tune-Up / Preventative Maintenance" data-testid="new-job-type-input" className="mt-1" />
              </div>
              <div>
                <Label>Flat pay</Label>
                <Input type="number" step="0.01" value={newAmount} onChange={(e) => setNewAmount(e.target.value)} placeholder="0.00" data-testid="new-flat-amount-input" className="mt-1" />
              </div>
              <Button type="button" onClick={addEntry} data-testid="add-rate-row-btn"><Plus className="h-4 w-4 mr-1" /> Add</Button>
            </div>
            {presetJobTypes.length > 0 && (
              <div className="pt-2 text-xs text-slate-500">
                <span className="font-semibold text-slate-700">Suggested from preset:</span>{" "}
                {presetJobTypes.map((jt) => (
                  <button
                    key={jt}
                    type="button"
                    onClick={() => setNewJob(jt)}
                    data-testid={`suggest-${jt.replace(/\s+/g, "_")}`}
                    className="inline-block mr-1.5 mt-1 px-2 py-0.5 rounded-full bg-slate-100 hover:bg-sky-100 text-slate-700"
                  >
                    {jt}
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
